import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { toDataURL } from 'qrcode';
import { AuthenticationService } from '../../../../services/guards/authentication.service';
import { IMeResponse, UserService } from '../../../../services/api/auth/user.service';
import { PlanEnum } from '../../../../models/user';
import { CookieService } from 'src/app/services/utils/cookie.service';
import {
    LOCAL_STORAGE_INITIAL_URL_PARAMS_KEY,
    LOCAL_STORAGE_RETURN_URL_KEY,
} from '../../../../constants/shared/local-storage.constants';
import { ProjectStatusEnum } from '../../../../models/defines';
import { ProjectGeneralRouterService } from '../../../../services/routes/dashboard/project/project-general-router.service';
import {
    AuthenticationResponseJSON,
    RegistrationResponseJSON,
    StrongAuthLoginResponse,
    StrongAuthLoginState,
    StrongAuthMethod,
    TotpEnrollmentResponse,
} from '../../../../models/security/security.models';
import { SecurityApiService } from '../../../../services/security/security-api.service';
import { PasskeyBrowserService } from '../../../../services/security/passkey-browser.service';
import { getStrongAuthErrorPresentation } from './strong-auth-error-message';

type LoginView = 'loading' | 'method-choice' | 'totp-code' | 'totp-enrollment' | 'error';

@Component({
    selector: 'app-sign-in-success',
    templateUrl: './login-success.component.html',
    styleUrls: ['./login-success.component.scss'],
})
export class LoginSuccessComponent implements OnInit {
    chosenPlan: PlanEnum | null = null;
    view: LoginView = 'loading';
    methods: StrongAuthMethod[] = [];
    enrollmentRequired = false;
    continuation = '';
    totpCode = '';
    totpEnrollment: TotpEnrollmentResponse | null = null;
    qrCodeDataUrl = '';
    isBusy = false;
    errorMessage = '';
    passkeyRetryAvailable = false;
    recoveryStatusMessage = '';
    browserStatusMessage = '';
    showBrowserSignInLink = false;

    public readonly StrongAuthMethod = StrongAuthMethod;

    constructor(
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private authentication: AuthenticationService,
        private userAPI: UserService,
        private cookieService: CookieService,
        private projectGeneralRouting: ProjectGeneralRouterService,
        private securityApi: SecurityApiService,
        public passkeys: PasskeyBrowserService
    ) {}

    public ngOnInit(): void {
        this.restoreChosenPlan();
        this.activatedRoute.paramMap.subscribe((params: ParamMap) => {
            const continuation = params.get('token');
            if (!continuation) {
                this.showError('This sign-in link is incomplete. Start again from the sign-in page.');
                return;
            }
            this.continuation = continuation;
            void this.resolveLoginAsync();
        });
    }

    public async usePasskeyAsync(): Promise<void> {
        if (this.isBusy) return;
        if (!this.passkeys.isSupported()) {
            this.showError('Passkeys are not available in this browser. Use an authenticator app instead.');
            return;
        }
        await this.runBusyAsync(async () => {
            if (this.enrollmentRequired) {
                const enrollment = await firstValueFrom(
                    this.securityApi.startLoginPasskeyEnrollment$(this.continuation, this.getDeviceName())
                );
                const response = (await this.passkeys.register(enrollment.options)) as RegistrationResponseJSON;
                const result = await firstValueFrom(
                    this.securityApi.confirmLoginPasskeyEnrollment$(
                        this.continuation,
                        enrollment.continuation,
                        response
                    )
                );
                await this.handleLoginResponseAsync(result);
                return;
            }

            const authentication = await firstValueFrom(this.securityApi.getLoginPasskeyOptions$(this.continuation));
            const response = (await this.passkeys.authenticate(authentication.options)) as AuthenticationResponseJSON;
            const result = await firstValueFrom(
                this.securityApi.verifyLoginPasskey$(this.continuation, authentication.continuation, response)
            );
            await this.handleLoginResponseAsync(result);
        });
    }

    public async useAuthenticatorAsync(): Promise<void> {
        if (this.isBusy) return;
        this.errorMessage = '';
        if (!this.enrollmentRequired) {
            this.view = 'totp-code';
            return;
        }
        await this.runBusyAsync(async () => {
            this.totpEnrollment = await firstValueFrom(this.securityApi.startLoginTotpEnrollment$(this.continuation));
            this.qrCodeDataUrl = await toDataURL(this.totpEnrollment.otpauthUri, {
                width: 208,
                margin: 1,
                color: { dark: '#1a1a1a', light: '#ffffff' },
            });
            this.view = 'totp-enrollment';
        });
    }

    public async submitTotpAsync(): Promise<void> {
        this.totpCode = this.totpCode.replace(/[\s-]/g, '');
        if (!/^\d{6}$/.test(this.totpCode)) return;
        await this.runBusyAsync(async () => {
            const result = this.enrollmentRequired
                ? await firstValueFrom(
                      this.securityApi.confirmLoginTotpEnrollment$(
                          this.continuation,
                          this.totpEnrollment.continuation,
                          this.totpCode
                      )
                  )
                : await firstValueFrom(this.securityApi.verifyLoginTotp$(this.continuation, this.totpCode));
            await this.handleLoginResponseAsync(result);
        });
    }

    public async refreshVerificationAsync(): Promise<void> {
        if (!this.continuation) return;
        await this.runBusyAsync(async () => {
            const response = await firstValueFrom(this.securityApi.resumeLogin$(this.continuation));
            await this.handleLoginResponseAsync(response);
            if (response.state === StrongAuthLoginState.VerificationRequired) {
                this.recoveryStatusMessage =
                    'Verification is still required. Ask your organization owner or manager to approve account recovery, then check again. If you can’t reach them, contact Shuffll support.';
            }
        });
    }

    public get browserSignInUrl(): string {
        return `${window.location.origin}/onboarding/security/${encodeURIComponent(this.continuation)}`;
    }

    public async copyBrowserSignInLinkAsync(): Promise<void> {
        if (this.isBusy || !this.continuation) return;
        try {
            await navigator.clipboard.writeText(this.browserSignInUrl);
            this.showBrowserSignInLink = false;
            this.browserStatusMessage =
                'Link copied. Paste it into the address bar in Chrome, Edge, or Safari to continue signing in.';
        } catch {
            this.showBrowserSignInLink = true;
            this.browserStatusMessage =
                'Copy the link below and paste it into the address bar in Chrome, Edge, or Safari.';
        }
    }

    public pasteTotpCode(event: ClipboardEvent): void {
        event.preventDefault();
        const code = (event.clipboardData?.getData('text') ?? '').replace(/[\s-]/g, '');
        if (/^\d{6}$/.test(code)) this.totpCode = code;
    }

    public returnToMethods(): void {
        this.totpCode = '';
        this.errorMessage = '';
        this.view = 'method-choice';
    }

    private async resolveLoginAsync(): Promise<void> {
        await this.runBusyAsync(async () => {
            // Existing links and OAuth redirects contain server-signed session JWTs.
            // Keep them usable while the client and server roll out separately;
            // finishLoginAsync validates the token through /me before navigation.
            if (/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(this.continuation)) {
                await this.finishLoginAsync(this.continuation);
                return;
            }
            const isLoginContinuation = this.activatedRoute.snapshot.data?.continuationPurpose === 'login';
            const response = await firstValueFrom(
                isLoginContinuation
                    ? this.securityApi.resumeLogin$(this.continuation)
                    : this.securityApi.completeLogin$(this.continuation)
            );
            if (!isLoginContinuation && response.state !== StrongAuthLoginState.Complete && response.continuation) {
                await this.router.navigate(['/onboarding/security', response.continuation], { replaceUrl: true });
                return;
            }
            await this.handleLoginResponseAsync(response);
        });
    }

    private async handleLoginResponseAsync(response: StrongAuthLoginResponse): Promise<void> {
        if (response.state === StrongAuthLoginState.Complete && response.token) {
            await this.finishLoginAsync(response.token);
            return;
        }
        if (!response.continuation) throw new Error('The sign-in challenge could not be created.');
        this.continuation = response.continuation;
        this.methods = response.methods;
        this.enrollmentRequired = response.state === StrongAuthLoginState.EnrollmentRequired;
        this.view = 'method-choice';
    }

    private async finishLoginAsync(token: string): Promise<void> {
        this.view = 'loading';
        this.authentication.login(token);
        try {
            const data: IMeResponse = await this.userAPI.me();
            const user = data?.user;
            if (!user) throw new Error('User profile was not returned');
            await this.userAPI.updateDBandConvertkit(user.email, user.name);
            this.authentication.saveUser(user);
            await this.navigateAfterLoginAsync();
        } catch {
            this.authentication.logout('');
            this.showError('Your security check succeeded, but your account could not be loaded. Sign in again.');
        }
    }

    private async navigateAfterLoginAsync(): Promise<void> {
        let returnUrl = localStorage.getItem(LOCAL_STORAGE_RETURN_URL_KEY);
        const initialUrlParamsStr = localStorage.getItem(LOCAL_STORAGE_INITIAL_URL_PARAMS_KEY);
        let initialUrlParams = {};
        if (initialUrlParamsStr) {
            try {
                initialUrlParams = JSON.parse(initialUrlParamsStr);
            } catch {
                initialUrlParams = {};
            }
        }
        if (!returnUrl) {
            await this.router.navigate(['/dashboard']);
            return;
        }
        if (
            returnUrl.includes('dashboard') ||
            returnUrl.includes('onboarding/authenticate') ||
            returnUrl.includes('onboarding/security')
        ) {
            returnUrl = '/dashboard';
        } else if (returnUrl.includes('projectId')) {
            const projectId = JSON.parse(returnUrl).projectId;
            await this.projectGeneralRouting.goToAsync(ProjectStatusEnum.PLANNING, projectId, true);
            return;
        }
        await this.router.navigate([decodeURI(returnUrl)], { queryParams: initialUrlParams });
    }

    private async runBusyAsync(action: () => Promise<void>): Promise<void> {
        if (this.isBusy) return;
        this.isBusy = true;
        this.errorMessage = '';
        this.recoveryStatusMessage = '';
        this.passkeyRetryAvailable = false;
        try {
            await action();
        } catch (error) {
            const presentation = getStrongAuthErrorPresentation(error, {
                hasAuthenticatorFallback: this.enrollmentRequired || this.methods.includes(StrongAuthMethod.Totp),
            });
            this.errorMessage = presentation.message;
            this.passkeyRetryAvailable = presentation.retryPasskey === true;
            if (presentation.terminal || this.view === 'loading') this.view = 'error';
        } finally {
            this.isBusy = false;
        }
    }

    private showError(message: string): void {
        this.errorMessage = message;
        if (this.view === 'loading') this.view = 'error';
    }

    private restoreChosenPlan(): void {
        const chosenPlanString = this.cookieService.getCookie('chosenPlan');
        if (!chosenPlanString) return;
        this.chosenPlan = JSON.parse(chosenPlanString) as PlanEnum;
        if (this.authentication.user) this.authentication.user.plan = this.chosenPlan;
    }

    private getDeviceName(): string {
        const platform = navigator.platform?.trim();
        return platform ? `${platform} passkey` : 'My passkey';
    }
}
